import React, { useContext, useState } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';

import { useTheme } from '../../../../contexts/ThemeContext';
import { PersonaChatUIContext } from '../../../../contexts/PersonaChatUIContext';
import { usePersonaName, usePersonaColor } from '../../../../hooks/usePersonaColor';
import { CampMenu } from '../campMenu/CampMenu';
import { CampBriefPopover } from '../campMenu/CampBriefPopover';
import type { SpineRootMarkerData } from '../types';

// Lazy so the node's eager import graph stays free of the popover's
// workService→api.ts chain (import.meta crashes CRA-jest, and this node is
// rendered by many suites). Loads only when the operator opens the editor.
const SpineHillEditPopover = React.lazy(() =>
  import('../SpineHillEditPopover').then((m) => ({ default: m.SpineHillEditPopover })));

/**
 * SpineRootMarkerNode — entry chip for a peer persona's spine chain.
 *
 * Renders compact: small persona-color dot + persona name + tiny "▸" pip
 * indicating the chain starts here. No verb chrome (cartographic
 * metaphor — "Persona X joined" was meeting-room language).
 *
 * Persona data via hooks (null-safe). Direct useContext on
 * CouncilContext is forbidden here — go through usePersonaName /
 * usePersonaColor so context-absent sessions don't crash.
 */
const SpineRootMarkerNode: React.FC<NodeProps<SpineRootMarkerData>> = ({ data }) => {
  const { darkMode } = useTheme();
  const personaName = usePersonaName(data.personaId);
  const isStandby = data.arrivalState === 'standby';
  const color = usePersonaColor({
    id: data.personaId,
    accent_color: undefined,
    name: personaName,
  });
  // SP4 J.2 — cross-panel hover glow. CitationPill writes the target
  // spine-root message id on hover; we ring this node when it matches.
  const uiCtx = useContext(PersonaChatUIContext);
  const isGlowing = uiCtx?.citationGlowTargetId
    && uiCtx.citationGlowTargetId === data.spineRootMessageId;
  // Camp-pickup v1 — one node overlay at a time: the hill editor (via the
  // ⋯ menu's set_goal) or the peek brief. Mutual exclusion by construction.
  const [overlay, setOverlay] = useState<'none' | 'edit' | 'peek'>('none');
  // SP-2 — a blueprint intent root renders as the CARD'S LANE-HEAD BAND: an
  // amber, card-width band that reads as the bottom of the blueprint card the
  // spine hangs from (vs. the free persona chip every other spine root uses).
  const laneHead = data.spineRole === 'card-lane-head';
  const shellClass = laneHead
    ? (darkMode
        ? 'bg-gray-800 border-amber-500/40 text-gray-200 rounded-t-none rounded-b-md border-t-0'
        : 'bg-amber-50 border-amber-400 text-gray-800 rounded-t-none rounded-b-md border-t-0')
    : (darkMode
        ? 'bg-gray-800 border-gray-600/80 text-gray-200 rounded-md'
        : 'bg-white border-gray-300 text-gray-800 rounded-md');

  return (
    <div
      className={`nodrag flex items-center gap-1.5 px-2 py-1 border text-[11px] select-none transition-all ${shellClass} ${isGlowing ? 'ring-2 ring-violet-400 animate-pulse' : ''} ${isStandby ? 'opacity-70' : ''}`}
      data-testid={`spine-root-marker-${data.personaId}`}
      style={{ minWidth: laneHead ? 420 : 180, position: 'relative' }}
    >
      <Handle type="target" id="t" position={Position.Top} style={{ opacity: 0 }} />
      <span
        aria-hidden
        style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: isStandby ? 'transparent' : color.hex,
          border: isStandby ? `1.5px solid ${color.hex}` : undefined,
          flexShrink: 0,
        }}
      />
      <span className="flex flex-col min-w-0" style={{ flex: 1 }}>
        <span className="font-medium truncate">{personaName}</span>
        {data.task && (
          <span
            data-testid={`spine-root-task-${data.personaId}`}
            className={`truncate text-[10px] ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
            title={data.task}
          >
            {data.task}
          </span>
        )}
        {data.arrivalState === 'pending' && (
          <span
            data-testid={`spine-root-pending-${data.personaId}`}
            className={`truncate text-[10px] italic animate-pulse ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
          >
            {personaName} is reading the trail…
          </span>
        )}
        {data.arrivalState === 'standby' && (
          <span
            data-testid={`spine-root-standby-${data.personaId}`}
            title="Consult to bring them in"
            className={`truncate text-[10px] italic ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
          >
            · making camp
          </span>
        )}
        {data.arrivalState === 'failed' && (
          <span
            data-testid={`spine-root-failed-${data.personaId}`}
            className={`truncate text-[10px] ${darkMode ? 'text-red-300' : 'text-red-600'}`}
            title={data.turnFailedReason}
          >
            ⚠ turn failed{data.turnFailedReason ? `: ${data.turnFailedReason}` : ''}
          </span>
        )}
        {data.arrivalState === 'merged' && (
          <span
            data-testid={`spine-root-merged-${data.personaId}`}
            className={`truncate text-[10px] ${darkMode ? 'text-amber-300' : 'text-amber-600'}`}
            title="This companion's work was folded back into the main trail"
          >
            ↩ folded into main
          </span>
        )}
        {data.resolvedTarget && (() => {
          // Camp-pickup v1 — chip is informational only; editing moved to the
          // ⋯ menu's set_goal → node-level popover.
          const rt = data.resolvedTarget!;
          const chipClass = `truncate text-[10px] ${
            rt.source === 'spine'
              ? (darkMode ? 'text-indigo-300' : 'text-indigo-600')
              : (darkMode ? 'text-gray-500 italic' : 'text-gray-400 italic')
          }`;
          const titleAttr = rt.source === 'spine'
            ? `Own spine target: ${rt.title}`
            : `Inherited from ${rt.source}: ${rt.title}`;
          return (
            <span data-testid={`spine-root-target-${data.personaId}`} title={titleAttr} className={chipClass}>
              {rt.source !== 'spine' && (
                <span className="sr-only">inherited from {rt.source}: </span>
              )}
              <span aria-hidden>⛰ {rt.source !== 'spine' && '↑ '}</span>
              {rt.title}
            </span>
          );
        })()}
      </span>
      <span
        className={isStandby ? (darkMode ? 'text-gray-600' : 'text-gray-300') : (darkMode ? 'text-gray-400' : 'text-gray-500')}
        aria-label="chain starts here"
      >
        ▸
      </span>
      {/* C2.2 — operator fold-back affordance. Only on the Trailhead surface
          (canFoldBack) and only once the companion's turn has landed
          (arrivalState 'resolved'); a merged/pending/standby camp shows nothing. */}
      {uiCtx?.canFoldBack && data.arrivalState === 'resolved' && (
        <button
          type="button"
          data-testid={`spine-root-foldback-${data.personaId}`}
          onClick={(e) => { e.stopPropagation(); uiCtx.openFoldBack(data.spineRootMessageId); }}
          title="Fold this companion's findings back into the main trail"
          className={`nodrag ml-1 text-[10px] px-1 rounded ${
            darkMode ? 'text-amber-300 hover:bg-amber-900/40' : 'text-amber-600 hover:bg-amber-100'
          }`}
        >
          ↩ fold back
        </button>
      )}
      {uiCtx?.canFoldBack && (
        <CampMenu
          context={{
            personaId: data.personaId,
            personaName,
            spineRootMessageId: data.spineRootMessageId,
            arrivalState: data.arrivalState ?? 'standby', // standby = inert default; 'resolved' would falsely offer fold_back
            canEditHill: !!uiCtx?.canEditHill,
            canInvite: !!uiCtx?.canInvitePeer,
            trailId: data.trailId ?? null,

            resolvedTarget: data.resolvedTarget,
            task: data.task,
          }}
          onSetGoal={() => setOverlay('edit')}
          onPeekBrief={() => setOverlay('peek')}
        />
      )}
      {overlay === 'peek' && (
        <CampBriefPopover
          personaId={data.personaId}
          personaName={personaName}
          task={data.task}
          arrivalState={data.arrivalState ?? 'standby'}
          turnFailedReason={data.turnFailedReason}
          onClose={() => setOverlay('none')}
        />
      )}
      {overlay === 'edit' && (
        <React.Suspense fallback={null}>
          <SpineHillEditPopover
            current={data.resolvedTarget ?? null}
            hasOwnSpineTarget={data.resolvedTarget?.source === 'spine'}
            onSet={async (target) => { await uiCtx!.editHillTarget({ scope_kind: 'spine', scope_ref: data.spineRootMessageId, target }); setOverlay('none'); }}
            onFork={async (target) => { await uiCtx!.forkHillTarget({ persona_id: data.personaId, current_spine_root_message_id: data.spineRootMessageId, scope_kind: 'spine', target }); setOverlay('none'); }}
            onClose={() => setOverlay('none')}
          />
        </React.Suspense>
      )}
      <Handle type="source" id="b" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
};

export default SpineRootMarkerNode;
